// Accepts only non-empty http(s) URLs coming from the feed payload
function safeUrl(value, fallback) {
  // new URL(undefined, origin) would resolve to a valid shop URL - reject non-strings first
  if (typeof value !== 'string' || value.trim() === '') return fallback;

  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : fallback;
  } catch {
    return fallback;
  }
}

class ScInstagramFeed extends HTMLElement {
  connectedCallback() {
    // Delegated, so tiles the live feed renders later are covered too. Bound before
    // the design-mode bail-out: play has to work while editing as well.
    this.addEventListener('click', (event) => {
      const play = event.target.closest('.sc-ig__play');
      if (play) return this.playTile(play);

      const sound = event.target.closest('.sc-ig__sound');
      if (sound) return this.toggleSound(sound);

      // Tapping the picture is how a reel is paused and resumed. The play button
      // comes back while paused, so the same thing stays reachable by keyboard.
      const video = event.target.closest('.sc-ig__video');
      if (video) this.togglePlayback(video);
    });

    // Keep the curated blocks selectable in the theme editor
    if (window.Shopify && window.Shopify.designMode) return;

    this.grid = this.querySelector('[data-sc-ig-grid]');
    if (!this.dataset.endpoint || !this.grid) return; // curated posts stay

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.loadFeed();
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  // The video markup sits in a template, so nothing is fetched until this runs.
  playTile(button) {
    const media = button.closest('.sc-ig__media');
    if (!media) return;

    // Second press and later: the template is gone because the video was already
    // built from it, so there is nothing to construct - just resume. Without this
    // the method bailed out here and a paused tile could never be restarted.
    const existing = media.querySelector('.sc-ig__video');
    if (existing) {
      button.hidden = true;
      this.start(existing);
      return;
    }

    const template = media.querySelector('[data-sc-ig-video-source]');
    if (!template) return;

    const video = template.content.firstElementChild.cloneNode(true);
    media.classList.add('is-playing');
    template.remove();
    media.appendChild(video);

    // The play button stays in the DOM and only hides: it is the keyboard route
    // back into playback once the video is paused.
    button.hidden = true;

    const sound = media.querySelector('.sc-ig__sound');
    if (sound) sound.hidden = false;

    video.addEventListener('pause', () => media.classList.remove('is-playing'));
    video.addEventListener('play', () => media.classList.add('is-playing'));
    // Expired Instagram CDN links and broken sources both surface here.
    video.addEventListener('error', () => this.reset(video));

    this.start(video);
  }

  start(video) {
    // (4) Only one reel at a time. They loop and start unmuted, so a second tile
    // would otherwise play over the first.
    this.querySelectorAll('.sc-ig__video').forEach((other) => {
      if (other !== video && !other.paused) other.pause();
    });

    // Sound is on from the first press: the click is a user gesture, so the
    // browser allows it.
    const played = video.play();

    // (1) A rejected play, or a source that never loads, used to leave a black
    // square: is-playing hides the poster and the play button, and nothing put
    // them back. Failure has to undo that.
    if (played && typeof played.catch === 'function') played.catch(() => this.reset(video));
  }

  // Back to poster plus play button, whatever went wrong.
  reset(video) {
    const media = video.closest('.sc-ig__media');
    if (!media) return;

    media.classList.remove('is-playing');
    const button = media.querySelector('.sc-ig__play');
    if (button) button.hidden = false;
  }

  togglePlayback(video) {
    const media = video.closest('.sc-ig__media');
    const button = media && media.querySelector('.sc-ig__play');

    if (video.paused) {
      if (button) button.hidden = true;
      this.start(video);
    } else {
      video.pause();
      if (button) button.hidden = false;
    }
  }

  toggleSound(button) {
    const media = button.closest('.sc-ig__media');
    const video = media && media.querySelector('.sc-ig__video');
    if (!video) return;

    video.muted = !video.muted;
    button.setAttribute('aria-pressed', String(video.muted));
    button.setAttribute(
      'aria-label',
      video.muted ? this.dataset.unmuteLabel || 'Unmute' : this.dataset.muteLabel || 'Mute'
    );
  }

  async loadFeed() {
    try {
      const response = await fetch(this.dataset.endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      const items = Array.isArray(payload) ? payload : payload.data;
      if (!Array.isArray(items) || items.length === 0) return;

      this.render(items.slice(0, Number(this.dataset.limit) || 8));
    } catch (error) {
      console.warn('[sc-instagram-feed] Feed not loaded, keeping curated posts:', error);
    }
  }

  render(items) {
    const fallbackUrl = this.dataset.profileUrl;
    const label = this.dataset.label;
    const curated = Array.from(this.grid.children);
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const videoUrl = item.media_type === 'VIDEO' ? safeUrl(item.media_url, '') : '';

      // A reel does not always come with a thumbnail_url. Keying the tile off the
      // poster alone dropped exactly those posts, although the video itself plays;
      // the video element renders its own first frame when no poster is given.
      const posterSource = item.media_type === 'VIDEO' ? item.thumbnail_url : item.media_url;
      const imageUrl = safeUrl(posterSource, '');
      if (!imageUrl && !videoUrl) return; // nothing usable in this post

      const caption = (item.caption || '').trim().slice(0, 120) || label;

      const image = document.createElement('img');
      image.className = 'sc-ig__image';
      // loading must be set before src, or the browser starts fetching eagerly
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 600;
      image.height = 600;
      image.alt = caption;
      image.src = imageUrl;

      const listItem = document.createElement('li');
      listItem.className = 'sc-ig__item';

      // (5) Only a picture tile is worth dropping when its image 403s. On a video
      // tile the poster is decoration - removing it would take a playable video
      // with it.
      if (!videoUrl) {
        // Instagram CDN URLs are signed and expire - drop dead tiles instead of showing them
        image.addEventListener('error', () => this.dropTile(listItem, curated), { once: true });
      }

      // A video post keeps its thumbnail as the poster and gets the same play
      // button as a curated one, instead of linking away to Instagram.
      if (videoUrl) {
        const media = document.createElement('div');
        media.className = 'sc-ig__media';
        media.appendChild(image);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sc-ig__play';
        // Same label as a curated tile, from the section. The caption describes the
        // post, not what the button does.
        button.setAttribute('aria-label', this.dataset.playLabel || caption);
        button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>';
        media.appendChild(button);

        const template = document.createElement('template');
        template.setAttribute('data-sc-ig-video-source', '');
        const sound = document.createElement('button');
        sound.type = 'button';
        sound.className = 'sc-ig__sound';
        sound.hidden = true;
        sound.setAttribute('aria-pressed', 'false');
        sound.setAttribute('aria-label', this.dataset.muteLabel || 'Mute');
        sound.innerHTML =
          '<svg class="sc-ig__sound-on" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16 8.5a4.5 4.5 0 0 1 0 7M18.5 6a8 8 0 0 1 0 12" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>' +
          '<svg class="sc-ig__sound-off" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor"/><path d="M16.5 9.5l5 5m0-5l-5 5" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>';
        media.appendChild(sound);

        const video = document.createElement('video');
        video.className = 'sc-ig__video';
        video.playsInline = true;
        video.loop = true;
        video.preload = 'auto';
        video.poster = imageUrl;
        video.src = videoUrl;
        template.content.appendChild(video);
        media.appendChild(template);

        listItem.appendChild(media);
      } else {
        const link = document.createElement('a');
        link.className = 'sc-ig__link';
        link.href = safeUrl(item.permalink, fallbackUrl);
        link.target = '_blank';
        link.rel = 'noopener';
        // per-post label, so screen readers can tell the tiles apart
        link.setAttribute('aria-label', caption);
        link.appendChild(image);
        listItem.appendChild(link);
      }

      fragment.appendChild(listItem);
    });

    if (!fragment.childElementCount) return;

    // (7) The observer fires 200px early, so someone can already have started a
    // curated video when the feed lands. Swapping the grid would rip it out
    // mid-playback; the live posts are not worth that.
    const playing = this.grid.querySelector('.sc-ig__video');
    if (playing && !playing.paused) return;

    this.grid.replaceChildren(fragment);
  }

  dropTile(listItem, curated) {
    listItem.remove();

    // Every live image failed - bring the curated posts back
    if (this.grid.children.length === 0 && curated.length > 0) {
      this.grid.replaceChildren(...curated);
    }
  }
}

if (!customElements.get('sc-instagram-feed')) {
  customElements.define('sc-instagram-feed', ScInstagramFeed);
}
